import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, ShoppingCart, ArrowLeft } from 'lucide-react';
import { WishlistButton } from '@/components/WishlistButton';
import { formatBHD } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

interface ProductImage {
  url: string;
  position: number;
  alt_text?: string;
}

interface ProductData {
  id: string;
  name: string;
  price: number;
  sale_price?: number;
  description?: string;
  short_description?: string;
  sku?: string;
  stock_status: string;
  stock_quantity?: number;
  rating_average?: number;
  rating_count?: number;
  category?: string;
  product_images: ProductImage[];
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [product, setProduct] = useState<ProductData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    async function fetchProduct() {
      if (!id) return;

      try {
        const { data, error } = await supabase
          .from('products')
          .select(`
            id,
            name,
            price,
            sale_price,
            description,
            short_description,
            sku,
            stock_status,
            stock_quantity,
            rating_average,
            rating_count,
            product_images (
              url,
              position,
              alt_text
            )
          `)
          .eq('id', id)
          .single();

        if (error) {
          console.error('Error fetching product:', error);
          toast({
            title: 'Error',
            description: 'Failed to load product details',
            variant: 'destructive',
          });
          navigate('/shop');
          return;
        }

        if (data) {
          const sortedImages = (data.product_images || []).sort(
            (a, b) => a.position - b.position
          );
          setProduct({
            ...data,
            product_images: sortedImages,
          });
        }
      } catch (error) {
        console.error('Error:', error);
        toast({
          title: 'Error',
          description: 'An unexpected error occurred',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    }

    fetchProduct();
  }, [id, navigate, toast]);

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading product...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">Product Not Found</h1>
            <Button onClick={() => navigate('/shop')}>Back to Shop</Button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const hasDiscount = product.sale_price && product.sale_price < product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.price - product.sale_price) / product.price) * 100)
    : 0;

  const displayPrice = hasDiscount ? product.sale_price : product.price;
  const images = product.product_images.length > 0
    ? product.product_images
    : [{ url: '/placeholder-product.png', position: 0 }];

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Product Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative aspect-square overflow-hidden rounded-lg bg-muted">
                <img
                  src={images[selectedImage]?.url || images[0].url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = '/placeholder-product.png';
                  }}
                />
                {hasDiscount && (
                  <Badge className="absolute top-4 left-4 bg-red-500 hover:bg-red-600 text-white text-lg px-3 py-1">
                    {discountPercent}% OFF
                  </Badge>
                )}
              </div>

              {/* Thumbnail Images */}
              {images.length > 1 && (
                <div className="grid grid-cols-4 gap-2">
                  {images.slice(0, 4).map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImage(i)}
                      className={`aspect-square rounded-md overflow-hidden border-2 transition-all ${
                        selectedImage === i
                          ? 'border-primary'
                          : 'border-transparent hover:border-muted-foreground/30'
                      }`}
                    >
                      <img
                        src={img.url}
                        alt={`${product.name} ${i + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder-product.png';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                {product.category && (
                  <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">
                    {product.category}
                  </p>
                )}
                <h1 className="text-3xl lg:text-4xl font-bold mb-3">
                  {product.name}
                </h1>

                {product.sku && (
                  <p className="text-sm text-muted-foreground mb-2">
                    SKU: {product.sku}
                  </p>
                )}

                {product.rating_average && (
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-5 w-5 ${
                            i < Math.floor(product.rating_average!)
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {product.rating_average.toFixed(1)}
                      {product.rating_count && ` (${product.rating_count} reviews)`}
                    </span>
                  </div>
                )}
              </div>

              {/* Price */}
              <div className="flex items-center gap-4 py-4 border-y border-border">
                {hasDiscount ? (
                  <>
                    <p className="text-4xl font-bold text-primary">
                      {formatBHD(product.sale_price!)}
                    </p>
                    <p className="text-2xl text-muted-foreground line-through">
                      {formatBHD(product.price)}
                    </p>
                    <Badge variant="destructive" className="text-base px-3 py-1">
                      Save {discountPercent}%
                    </Badge>
                  </>
                ) : (
                  <p className="text-4xl font-bold">
                    {formatBHD(product.price)}
                  </p>
                )}
              </div>

              {/* Stock Status */}
              <div className="flex items-center gap-2">
                {product.stock_status === 'instock' ? (
                  <>
                    <div className="h-3 w-3 bg-green-500 rounded-full"></div>
                    <span className="text-green-600 font-medium">In Stock</span>
                    {product.stock_quantity && product.stock_quantity > 0 && (
                      <span className="text-muted-foreground">
                        ({product.stock_quantity} available)
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <div className="h-3 w-3 bg-red-500 rounded-full"></div>
                    <span className="text-red-600 font-medium">Out of Stock</span>
                  </>
                )}
              </div>

              {/* Description */}
              {(product.description || product.short_description) && (
                <div className="prose prose-sm max-w-none">
                  <h3 className="text-lg font-semibold mb-2">Description</h3>
                  <p className="text-muted-foreground">
                    {product.description || product.short_description}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  size="lg"
                  className="w-full"
                  disabled={product.stock_status !== 'instock'}
                  onClick={() => {
                    toast({
                      title: 'Added to Cart',
                      description: `${product.name} has been added to your cart`,
                    });
                  }}
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  {product.stock_status === 'instock' ? 'Add to Cart' : 'Out of Stock'}
                </Button>

                <WishlistButton
                  productId={product.id}
                  className="w-full"
                  onClick={(e) => {
                    // WishlistButton handles its own click
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
